import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CustomModal from '../../Elements/modal/CustomModal'
import { selectIsNeededZIndex } from '../../../redux/selector/system'
import { ButtonsModalWrapper, InputReasonWrapper } from './index.styled'
import { reasonCancelItem } from './constants'
import { useTranslation } from 'react-i18next'
import KeyBindings from '../../Elements/keybindings'
import ErrorMessage from '../../Elements/message/ErrorMessage'
import { addTableCartCancelItem, addTableCartCancelSubItem, setTableCartItemToDelete } from '../../../redux/action/tableCart'
import ModalButton from '../../Elements/modal/ButtonsModal/ModalButton'
import Button from '../../Elements/buttons/Button'
import { FormButtons } from '../../Elements/form/index.styled'
import TextAreaInputControlled from '../../Elements/inputs/TextAreaInput/controlled'

const EVENT_Z_INDEX = 121

const CancelReasonModal = React.memo(() => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const isNeededZIndex = useSelector(selectIsNeededZIndex(EVENT_Z_INDEX))
	const inputRef = useRef(null)
	const [activeButtonIndex, setActiveButtonIndex] = useState(0)
	const [cancelReason, setCancelReason] = useState('')
	const [displayError, setDisplayError] = useState(false)
	const itemToDelete = useSelector(store => store.tableCart.itemToDelete)

	const buttons = reasonCancelItem?.map(reason =>
		({ icon: 'cancel', value: reason?.value, text: t(reason?.label), onClick: e => handleItemClick(e, reason.value), show: true }))
	const isButtonsDisabled = !itemToDelete

	useEffect(() => {
		if (itemToDelete) {
			setActiveButtonIndex(0)
		}
	}, [itemToDelete]) // eslint-disable-line react-hooks/exhaustive-deps

	const focusInput = useCallback((reason) => {
		if (reason === 'OTHER' && inputRef.current) {
			inputRef.current.focus()
		} else {
			setCancelReason('')
			setDisplayError(false)
			if (inputRef.current) {
				inputRef.current.blur() // Знімаємо фокус з інпута
			}
		}
	}, [inputRef])

	useEffect(() => {
		const reason = buttons[activeButtonIndex]?.value
		focusInput(reason)
	}, [focusInput, activeButtonIndex, buttons])

	const handleItemClick = useCallback((event, reason) => {
		if (event) event.preventDefault()
		focusInput(reason)
	}, [focusInput])

	const handleArrowUp = useCallback(() => {
		if (!buttons[activeButtonIndex - 1]) {
			setActiveButtonIndex(buttons.length - 1)
		} else {
			setActiveButtonIndex(activeButtonIndex - 1)
		}
	}, [buttons, activeButtonIndex])

	const handleArrowDown = useCallback(() => {
		if (!buttons[activeButtonIndex + 1]) {
			setActiveButtonIndex(0)
		} else {
			setActiveButtonIndex(activeButtonIndex + 1)
		}
	}, [buttons, activeButtonIndex])

	const handleButtonClick = useCallback((e, button, index) => {
		setActiveButtonIndex(index)
		button?.onClick(e)
	}, [])


	const handleChangeReason = event => {
		setDisplayError(false)
		setCancelReason(event.target.value)
	}

	const handleInputFocus = useCallback(() => {
		if (buttons[activeButtonIndex].value !== 'OTHER') {
			setActiveButtonIndex(2)
		}
	}, [buttons, activeButtonIndex])

	const handleClose = useCallback(() => {
		dispatch(setTableCartItemToDelete(null))
		setDisplayError(false)
		setCancelReason('')
	}, [dispatch])

	const handleSaveReason = useCallback(() => {
		const selectedBtn = buttons[activeButtonIndex]
		const trimmedReason = cancelReason.trim()
		if (!trimmedReason && selectedBtn.value === 'OTHER') {
			setDisplayError(true)
			return
		}

		const { subItemIndex, itemPositionId, orderId, subItemPositionId } = itemToDelete
		const prepareData = {
			itemPositionId,
			subItemPositionId,
			orderId,
			reasonType: selectedBtn.value,
			reason: trimmedReason,
		}
		if (!subItemIndex && subItemIndex !== 0) {
			dispatch(addTableCartCancelItem(prepareData))
		} else {
			dispatch(addTableCartCancelSubItem(prepareData))
		}

		dispatch(setTableCartItemToDelete(null))
	}, [activeButtonIndex, buttons, cancelReason, dispatch, itemToDelete])

	return (
		<>
			<CustomModal isOpen={itemToDelete}
									 close={handleClose}
									 size="small"
									 title={t('Cart.CancelReasonModal.title')}
									 id="cancel-reason-modal"
									 zIndex={EVENT_Z_INDEX}
									 disableBackButton={true}
									 returnZIndex={0}>
				<ButtonsModalWrapper>
					{buttons?.map((button, index, arr) => {
						const className = 'thinBorder ' + (index === arr.length - 1 ? 'noBottomRadius ' : '')
						return <ModalButton key={index}
																index={index}
																tabIndex={index}
																button={button}
																onClick={handleButtonClick}
																isDisabled={isButtonsDisabled}
																isActive={index === activeButtonIndex}
																className={className} />
					})}
				</ButtonsModalWrapper>
				<InputReasonWrapper>
					<TextAreaInputControlled name="reason"
																	 type="text"
																	 refInput={inputRef}
																	 onChange={handleChangeReason}
																	 onFocus={handleInputFocus}
																	 value={cancelReason}
																	 placeholder={t('Cart.CancelReasonModal.placeholder')}
																	 autoComplete="off"
																	 className="lf-border"
					/>
					{displayError && <ErrorMessage className="textLeft">{t('Cart.CancelReasonModal.error')}</ErrorMessage>}
				</InputReasonWrapper>
				<FormButtons>
					<Button icon="clear" keyButton="F3" text={t('buttons.controls.cancel')}
									zIndex={EVENT_Z_INDEX} onClick={handleClose} className="back" />
					<Button color="green" icon="checkmark" keyButton="F2" text={t('buttons.controls.ok')}
									zIndex={EVENT_Z_INDEX} onClick={handleSaveReason} testId="button-submit" />
				</FormButtons>

			</CustomModal>

			{isNeededZIndex &&
				<KeyBindings arrowUp={handleArrowUp}
										 arrowDown={handleArrowDown} />}
		</>
	)
})

CancelReasonModal.displayName = 'CancelReasonModal'
export default CancelReasonModal
